import API from "../Network/API"
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import AuthLayout, { AuthRoleTabs, PasswordInput, Spinner } from "./AuthLayout";

const EMAIL_RE = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w\w+)+$/;
// letters from any script (Arabic names included), spaces, apostrophes, dots and hyphens
const NAME_RE = /^[\p{L}\s'.-]*$/u;

// Shared sign-up form. role: "teacher" (type 1) or "student" (type 2).
export default function SignupForm({ role }) {
  const { t } = useTranslation();
  const isTeacher = role === "teacher";
  const ns = isTeacher ? "signup" : "signupStudent";
  const { user } = useSelector((state) => state.authReducer);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isTeacher && user?.role === "teacher") {
      window.location.href = '/dashboard/tutor';
    }
  }, [isTeacher, user?.role]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!EMAIL_RE.test(email)) {
      toast.error(t(`${ns}.email_not_correct`));
      return;
    }
    setLoading(true);
    setErrors({});
    API.post('/v1/oauth/register', {
      name: name.trim(),
      email,
      password,
      gender_id: gender,
      type: isTeacher ? 1 : 2,
    }).then(response => {
      setLoading(false);
      if (response.data.success === true) {
        localStorage.setItem(
          'xller_auth',
          JSON.stringify({
            user: response?.data?.data?.user,
            accessToken: response?.data?.data?.access_token,
          })
        )
        localStorage.setItem("xller_token", response?.data?.data?.access_token)
        toast.success(response.data.message, { duration: 3000 })
        const newRole = response?.data?.data?.user?.role;
        if (newRole === "teacher") {
          window.location.href = '/dashboard/tutor';
        } else if (newRole === "student") {
          window.location.href = '/home/student';
        } else {
          window.location.href = '/';
        }
      }
    }).catch((error) => {
      setLoading(false);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.response?.data?.message || t("auth.something_wrong"));
      }
    });
  }

  const fieldError = (key) => errors[key] && <div className="auth-error">{errors[key][0]}</div>;

  return (
    <AuthLayout
      tabs={<AuthRoleTabs active={role} />}
      title={isTeacher ? t("auth.signup_tutor_title") : t("auth.signup_student_title")}
      subtitle={isTeacher ? t("auth.signup_tutor_subtitle") : t("auth.signup_student_subtitle")}
      image={isTeacher ? "/assets/img/teach/teach-hero-tutor.jpg" : "/assets/img/hero/hero-learner.jpg"}
      accent={isTeacher ? "green" : "orange"}
      panelTitle={isTeacher ? t("auth.panel_tutor_title") : t("auth.panel_student_title")}
      panelPoints={isTeacher
        ? [t("becomeTeach.set_your_own_rate"), t("becomeTeach.teach_anytime"), t("auth.panel_tutor_point_3")]
        : [t("auth.panel_student_point_1"), t("auth.panel_student_point_2"), t("auth.panel_student_point_3")]}
      footer={
        <>
          <p className="auth-switch">
            {t(`${ns}.already_have_account`)} <Link to="/login">{t(`${ns}.login`)}</Link>
          </p>
          <p className="auth-legal">
            <Trans i18nKey="auth.signup_terms" components={{ termsLink: <Link to="/terms" />, privacyLink: <Link to="/privacy-policy" /> }} />
          </p>
        </>
      }
    >
      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-field">
          <label htmlFor="signup-name">{t(`${ns}.name`)}</label>
          <input
            id="signup-name"
            type="text"
            required
            minLength={3}
            autoComplete="name"
            className={`auth-input${errors.name ? " is-invalid" : ""}`}
            value={name}
            onChange={(e) => {
              const value = e.target.value;
              if (NAME_RE.test(value)) {
                setName(value);
              } else {
                toast.error(t(`${ns}.name_letters_only`), { id: "name-letters" });
              }
            }}
            placeholder={t(`${ns}.your_name`)}
          />
          {fieldError("name")}
        </div>

        <div className="auth-field">
          <label htmlFor="signup-gender">{t(`${ns}.gender`)}</label>
          <select
            id="signup-gender"
            required
            className={`auth-input auth-select${errors.gender_id ? " is-invalid" : ""}`}
            value={gender}
            onChange={(e) => setGender(e.target.value)}
          >
            <option value="">{t(`${ns}.select_gender`)}</option>
            <option value={1}>{t(`${ns}.male`)}</option>
            <option value={2}>{t(`${ns}.female`)}</option>
          </select>
          {fieldError("gender_id")}
        </div>

        <div className="auth-field">
          <label htmlFor="signup-email">{t(`${ns}.email`)}</label>
          <input
            id="signup-email"
            type="email"
            required
            autoComplete="email"
            className={`auth-input${errors.email ? " is-invalid" : ""}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t(`${ns}.your_email`)}
          />
          {fieldError("email")}
        </div>

        <div className="auth-field">
          <label htmlFor="signup-password">{t(`${ns}.password`)}</label>
          <PasswordInput
            id="signup-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t(`${ns}.your_password`)}
            autoComplete="new-password"
            invalid={Boolean(errors.password)}
          />
          {errors.password ? fieldError("password") : <div className="auth-hint">{t("auth.password_hint")}</div>}
        </div>

        <button type="submit" className="auth-submit" disabled={isLoading}>
          {isLoading ? <Spinner /> : null}
          {isTeacher ? t("auth.create_tutor_account") : t("auth.create_student_account")}
        </button>
      </form>
    </AuthLayout>
  );
}
